<template>
  <div id="team" class="flex">
    <div class="flex flex-col flex-grow">
      <div
        class="sticky left-0 z-50 flex items-center justify-between h-20 gap-4 px-6 border-b top-18 bg-slate-200 border-slate-400"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-4">
            <BlTextTitle4 text="Team" />
            <BlBubbleCount :count="totalUsers || 0" />
          </div>
          <BlTextBody2 text="Add and organize your team members." />
        </div>

        <BlTooltip>
          <template v-if="isDisabledInviteNewMember" v-slot:tooltip>
            <div class="flex flex-col gap-1 text-center w-44">
              <b>Reached team size limit</b>
              <div>
                Your center has reached the active member size limit. Please
                contact Blüm admin for assistance.
              </div>
            </div>
          </template>
          <BlInputButton
            icon="plus"
            label="Invite member"
            :disabled="isDisabledInviteNewMember"
            @clicked="newMemberOpened = true"
          />
        </BlTooltip>
      </div>

      <NewUserModal
        v-if="newMemberOpened"
        :centerId="currentCenter.id"
        @close-modal="newMemberOpened = false"
      />

      <div
        class="sticky left-0 z-30 flex items-center gap-2 px-6 py-3 mb-3 border-b border-slate-400"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <div class="w-40">
          <BlInputFilter @changed="onChangeFilter" :input="filterInput" />
        </div>
        <BlSort
          class="w-60"
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="defaultSort"
          @change="onChangeSort($event)"
        />
      </div>

      <div v-if="pageLoading" class="grid h-128 place-items-center">
        <div class="blum-loader"></div>
      </div>
      <div
        v-else-if="!users.length"
        class="flex flex-col items-center justify-center h-128"
      >
        <BlTextBody1 class="text-center" text="Team member(s) not found." />
      </div>
      <div v-else class="mb-8">
        <div
          id="team-header"
          class="sticky z-20 flex items-center flex-grow rounded-t top-38 bg-slate-300"
        >
          <div class="sticky left-0 z-10 flex pl-6 bg-slate-100">
            <BlTooltip
              class="flex items-center flex-shrink-0 h-12 px-4 border-t border-l border-r rounded-tl w-60 bg-slate-300 border-slate-400"
            >
              <BlTextSubtitle3 text="Name" class="truncate" />
            </BlTooltip>
          </div>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Email" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Role" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Status" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Account creation date" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 pl-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3
              text="Account deactivation date"
              class="truncate"
            />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Access permission" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Access tags" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-grow flex-shrink-0 w-32 h-12 px-4 border-t bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="" />
          </BlTooltip>
          <div class="sticky right-0 z-10 flex pr-6 bg-slate-100">
            <div
              class="flex items-center flex-shrink-0 w-12 h-12 px-4 border-t border-r rounded-tr bg-slate-300 border-slate-400"
            >
              <BlTextSubtitle3 text="" />
            </div>
          </div>
        </div>

        <TeamItem
          v-for="(user, idx) in users"
          :key="user.id"
          :user="user"
          :centerId="currentCenter.id"
          :isLast="idx + 1 === users.length"
        />
      </div>

      <div v-if="totalUsers > 25" class="flex mb-36">
        <BlInputPagination
          class="sticky left-6"
          :style="{ width: 'calc(100vw - 3rem - 0.5rem)' }"
          :page="page"
          :perPage="perPage"
          :total="totalUsers"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>
      <div v-else class="mb-46"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import debounce from "lodash/debounce";

import NewUserModal from "./NewUserModal.vue";
import TeamItem from "./Item.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlTooltip from "../BlTooltip.vue";
import BlIcon from "../BlIcon.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputFilter from "../BlInputFilter.vue";
import BlSort from "../BlSort.vue";
import BlInputPagination from "../BlInputPagination.vue";

export default {
  name: "Team",
  components: {
    NewUserModal,
    TeamItem,
    //
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlBubbleCount,
    BlTooltip,
    BlIcon,
    BlInputButton,
    BlInputSearch,
    BlInputFilter,
    BlSort,
    BlInputPagination
  },
  data() {
    const defaultSort = { primary: "date", secondary: "desc" };
    return {
      //
      pageLoading: true,
      newMemberOpened: false,
      //
      page: 1,
      perPage: 25,
      searchInput: { placeholder: "Search by name", model: "" },
      filterInput: {
        model: [[], [], []],
        tabs: [
          {
            name: "Role",
            kind: "multi",
            options: [
              { name: "Admin", value: "admin" },
              { name: "Therapist", value: "staff" }
            ]
          },
          {
            name: "Status",
            kind: "multi-tag",
            options: [
              {
                name: "Active",
                value: "active",
                color: "#789719",
                backgroundColor: "#F2F8CF"
              },
              {
                name: "Account created",
                value: "pending",
                color: "#564389",
                backgroundColor: "#E7E0EC"
              },
              {
                name: "Deactivated",
                value: "deactivated",
                color: "#667085",
                backgroundColor: "#F9FAFB"
              }
            ]
          },
          {
            name: "Access permission",
            kind: "multi",
            options: [
              { name: "All clients", value: "all_clients" },
              { name: "Assigned clients", value: "assigned_clients" }
            ]
          }
        ]
      },
      defaultSort,
      sortInput: defaultSort,
      sortOptions: {
        primary: [
          { value: "date", label: "Date created" },
          { value: "name", label: "Name" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "users", "totalUsers"]),
    isDisabledInviteNewMember() {
      return (
        this.currentCenter.total_active_members >=
        this.currentCenter.team_size_limit
      );
    },
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;

      const query = this.searchInput.model;
      const roles = this.filterInput.model[0];
      const statuses = this.filterInput.model[1];
      const restrictionTypes = this.filterInput.model[2];
      const sort = this.sortInput;

      if (query) params += `&query=${query}`;
      if (roles.length > 0) {
        params += `&role=${roles.map(a => a.value).join(",")}`;
      }
      if (statuses.length > 0) {
        params += `&status=${statuses.map(a => a.value).join(",")}`;
      }
      if (restrictionTypes.length > 0) {
        params += `&restriction_type=${restrictionTypes
          .map(a => a.value)
          .join(",")}`;
      }

      // - sort mapping -
      // date created, ascending -> oldest
      // date created, descending -> newest
      // name, ascending -> name_asc
      // name, descending -> name_desc
      // prettier-ignore
      const sorts = {
        date: { asc: "oldest", desc: "newest" },
        name: { asc: "name_asc", desc: "name_desc" }
      };
      params += `&sort=${sorts[sort.primary][sort.secondary]}`;

      return params;
    }
  },
  watch: {
    autogenerated(val) {
      this.customPasswordInput.disabled = val;
    }
  },
  mounted() {
    this.pageLoading = true;

    if (!this.currentUser.role.includes("admin")) {
      return this.$router.push({ name: "Clients" });
    }

    this.origin = window.location.origin;
    const searchParams = this.$route.query;

    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["query"];
    }

    const filter = [[], [], []];
    if (searchParams["role"]) {
      const arr = searchParams["role"].split(",").map(a => {
        const opts = this.filterInput.tabs[0].options;
        return opts.find(i => i.value === a) || { value: a };
      });
      filter[0] = arr;
    }
    if (searchParams["status"]) {
      const arr = searchParams["status"].split(",").map(a => {
        const opts = this.filterInput.tabs[1].options;
        return opts.find(i => i.value === a) || { value: a };
      });
      filter[1] = arr;
    }
    if (searchParams["restriction_type"]) {
      const arr = searchParams["restriction_type"].split(",").map(a => {
        const opts = this.filterInput.tabs[2].options;
        return opts.find(i => i.value === a) || { value: a };
      });
      filter[2] = arr;
    }
    this.filterInput.model = filter;

    // prettier-ignore
    const sorts = {
      oldest: { primary: "date", secondary: "asc" },
      newest: { primary: "date", secondary: "desc" },
      name_asc: { primary: "name", secondary: "asc" },
      name_desc: { primary: "name", secondary: "desc" }
    };
    if (searchParams["sort"]) {
      const sort = searchParams["sort"] || "";
      if (sort && sorts[sort]) {
        this.sortInput = sorts[sort];
      }
    }

    this.fetchUsers();
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) app.scroll({ top: 0, left: 0 });
  },
  methods: {
    ...mapActions(["getUsers"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
      setTimeout(() => {
        document.body.click();
      }, 10);
    },
    fetchUsers() {
      this.pageLoading = true;
      this.getUsers({ params: this.getParams }).then(() => {
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/team${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    //
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchUsers();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchUsers();
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchUsers();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchUsers();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchUsers();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/team/Team.vue